/** The plain verdict word of a filing, and the tone it is drawn in (ADR-0212 decision 6).
 *
 * The word names what was done, never what it permits (F07): "Checks passed" is not approval, and
 * only `approved` and `issued`, which have a person behind them, take the positive tone. The longer
 * sentences (`VERDICT_HEADLINE`) stay with the checks tab; this is the one or two words that sit
 * beside the percentage.
 */

import type { ChipTone } from "../../components/ui/StatusChip";
import { isIssuedState, stateWords } from "../filingState";
import type { FilingStateRead } from "../filingState";

export const VERDICT_WORD: Record<string, { word: string; tone: ChipTone }> = {
  not_evaluated: { word: "Not checked", tone: "neutral" },
  unsupported: { word: "Not supported", tone: "neutral" },
  incomplete: { word: "Incomplete", tone: "warn" },
  // "Required checks passed", not "Checks passed": the chip sat beside a tile that said "Checks
  // passed 7 of 9" and a "77% ready", and a reader took one of them to be wrong (design review
  // 2026-11-03). The words say which checks and that this is a pass of checks, not an approval.
  checks_passed: { word: "Required checks passed", tone: "info" },
  approved: { word: "Approved", tone: "ok" },
  issued: { word: "Issued", tone: "ok" },
};

/** A verdict this screen has never heard of is drawn as itself, neutral: not as a pass. */
export function verdictWord(verdict: string): { word: string; tone: ChipTone } {
  return VERDICT_WORD[verdict] ?? { word: verdict.replace(/_/g, " "), tone: "neutral" };
}

/** The word a filing's chip carries: the checks' verdict while nothing has been issued, and the
 * filing's state once something has - "Issued v2 - figures changed since - approval needed" - so the
 * chip cannot say a plain "Issued" over a filing whose approval lapsed or whose figures moved (Q-02).
 * Both are the server's: the verdict is the checks' word, the state is the filing's. */
export function headlineWord(
  verdict: string,
  state: FilingStateRead,
): { word: string; tone: ChipTone } {
  return isIssuedState(state) ? stateWords(state) : verdictWord(verdict);
}

/** The percentage as printed: the server's integer and a percent sign, or a dash. Never `0%` for
 * "nothing counted" and never `100%` unless the server said 100. */
export function percentText(value: number | null | undefined): string {
  return value === null || value === undefined ? "–" : `${String(value)}%`;
}
